import React, { useState, useEffect, useRef } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { X, UserPlus, UserMinus, Search, Users, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OrganisationWithDetails, OrganisationUser } from '../index'
import type { ModuleWithUsers } from './ModulesTab'

interface ModuleUsersModalProps {
  isOpen: boolean
  onClose: () => void
  module: ModuleWithUsers
  organisation: OrganisationWithDetails
  onUpdate: () => void
}

const CONNECT_USER_TO_MODULE = gql`
  mutation connectUserToModule($input: ConnectUserModuleInput!) {
    connectUserToModule(input: $input) {
      success
      message
    }
  }
`

const DISCONNECT_USER_FROM_MODULE = gql`
  mutation disconnectUserFromModule($userId: Int!, $moduleId: Int!, $subscriptionId: Int) {
    disconnectUserFromModule(userId: $userId, moduleId: $moduleId, subscriptionId: $subscriptionId) {
      success
      message
    }
  }
`

const ModuleUsersModal: React.FC<ModuleUsersModalProps> = ({ isOpen, onClose, module, organisation, onUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  // Get users available to add (org users not in this subscription)
  const connectedUserIds = new Set(module.connectedUsers.map((u) => u.id))
  const availableUsers = organisation.users.filter((u) => !connectedUserIds.has(u.id))

  const filteredAvailableUsers = availableUsers.filter(
    (user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.firstName && user.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.lastName && user.lastName.toLowerCase().includes(searchTerm.toLowerCase())),
  )

  const handleConnectUser = async (userId: number) => {
    setError(null)
    try {
      setSubmitting(true)
      const response = await authorizedGraphQLRequest<{
        connectUserToModule: { success: boolean; message: string }
      }>(CONNECT_USER_TO_MODULE, {
        input: {
          userId,
          moduleId: module.id,
          organisationId: organisation.id,
          subscriptionId: module.subscriptionId,
        },
      })

      if (response.connectUserToModule.success) {
        onUpdate()
      } else {
        setError(response.connectUserToModule.message)
      }
    } catch (err: any) {
      const message = err?.response?.errors?.[0]?.message || 'Failed to connect user to module'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDisconnectUser = async (userId: number) => {
    if (!confirm('Are you sure you want to remove this user from this subscription?')) return

    setError(null)
    try {
      setSubmitting(true)
      await authorizedGraphQLRequest(DISCONNECT_USER_FROM_MODULE, {
        userId,
        moduleId: module.id,
        subscriptionId: module.subscriptionId,
      })
      onUpdate()
    } catch (err: any) {
      const message = err?.response?.errors?.[0]?.message || 'Failed to disconnect user from module'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  const isAtCapacity = module.userCount >= module.maxUsers

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4 z-50" onClick={handleOutsideClick}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20" />

      {/* Modal */}
      <div ref={modalRef} className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col animate-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">{module.name} - Users</h2>
            <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
              <Users className="h-4 w-4" />
              {module.userCount} / {module.maxUsers} users assigned · subscription due {new Date(module.dueDate).toLocaleDateString()}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto p-1 hover:bg-red-100 rounded">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Connected Users Section */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">Connected Users ({module.connectedUsers.length})</h3>
            {module.connectedUsers.length === 0 ? (
              <div className="text-center text-gray-500 py-4 bg-gray-50 rounded-lg">
                <p>No users connected to this module</p>
              </div>
            ) : (
              <div className="space-y-2">
                {module.connectedUsers.map((user: OrganisationUser) => (
                  <div key={user.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/70 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold">{(user.firstName?.[0] || user.email[0]).toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email.split('@')[0]}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDisconnectUser(user.id)} disabled={submitting}>
                      <UserMinus className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Users Section */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-3">
              Add Users
              {isAtCapacity && <span className="ml-2 text-sm font-normal text-red-500">(At capacity)</span>}
            </h3>

            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                disabled={isAtCapacity}
              />
            </div>

            {/* Available Users List */}
            <div className="max-h-48 overflow-y-auto space-y-2">
              {availableUsers.length === 0 ? (
                <div className="text-center text-gray-500 py-4 bg-gray-50 rounded-lg">
                  <p>All organisation users are already connected</p>
                </div>
              ) : isAtCapacity ? (
                <div className="text-center text-gray-500 py-4 bg-gray-50 rounded-lg">
                  <p>Module is at capacity. Remove users to add more.</p>
                </div>
              ) : filteredAvailableUsers.length === 0 ? (
                <div className="text-center text-gray-500 py-4">
                  <p>No users found matching your search</p>
                </div>
              ) : (
                filteredAvailableUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-primary font-semibold">{(user.firstName?.[0] || user.email[0]).toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email.split('@')[0]}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    <Button size="sm" onClick={() => handleConnectUser(user.id)} disabled={submitting || isAtCapacity}>
                      <UserPlus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end bg-gray-50 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ModuleUsersModal
